import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { validateUserData } from '../schemas/userSchema';
import {
  validateFamilyMemberData,
  createFamilyMemberDocument
} from '../schemas/familyMemberSchema';
import { setupDefaultGroupAccess } from '../utils/accessControl';
import { relationshipService } from './relationshipService';
import { primaryFamilyCodeFromUserDoc } from '../utils/familyScope';

/**
 * Service for managing user profiles and family members
 */
class UserProfileService {
  /**
   * Check if an email already exists in the system
   * @param {string} email - Email address to check
   * @returns {Promise<{exists: boolean, userId: string|null}>}
   */
  async checkEmailExists(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    // Normalize email to lowercase for case-insensitive comparison
    const normalizedEmail = email.toLowerCase().trim();

    // Check in top-level users collection
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', normalizedEmail));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const existingUser = querySnapshot.docs[0];
      return {
        exists: true,
        userId: existingUser.id,
        userData: existingUser.data()
      };
    }

    return {
      exists: false,
      userId: null,
      userData: null
    };
  }

  /**
   * Initialize a new user profile
   * @param {string} userId - User ID
   * @param {Object} userData - User data including email, name, etc.
   * @param {string} familyCode - Family code
   * @param {string} relationship - Relationship to family owner (optional)
   */
  async initializeUserProfile(userId, userData, familyCode, relationship = 'other') {
    if (!userId || !userData || !familyCode) {
      throw new Error('User ID, user data, and family code are required');
    }

    const userProfile = {
      ...userData,
      familyCode,
      relationships: [],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'users', userId), userProfile);

    // Set up default group access based on relationship
    await setupDefaultGroupAccess(userId, familyCode, relationship);

    // If this is not the first family member, establish relationship with family owner
    const familyOwner = await this.getFamilyOwner(familyCode);
    if (familyOwner && familyOwner.id !== userId) {
      await relationshipService.addRelationship(familyOwner.id, userId, relationship, familyCode);
    }
  }

  /**
   * Get the family owner's profile
   * @param {string} familyCode - Family code
   * @returns {Object} Family owner's profile
   */
  async getFamilyOwner(familyCode) {
    const ownerDoc = await getDoc(doc(db, 'families', familyCode));
    if (!ownerDoc.exists()) {
      return null;
    }

    const ownerData = ownerDoc.data();
    const ownerProfile = await getDoc(doc(db, 'users', ownerData.ownerId));
    
    return {
      id: ownerProfile.id,
      ...ownerProfile.data()
    };
  }

  /**
   * Get user profile
   * @param {string} userId - User ID
   * @returns {Object} User profile
   */
  async getProfile(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  }

  /**
   * Create a new user profile (NESTED STRUCTURE)
   * Creates both top-level auth record and nested member document if familyCode provided
   * @param {Object} userData - User data to create profile with
   * @returns {Promise<string>} ID of created user
   */
  async createUserProfile(userData) {
    // Validate user data
    const validation = validateUserData(userData);
    if (!validation.isValid) {
      throw new Error(`Invalid user data: ${validation.errors.join(', ')}`);
    }

    // Check for duplicate email
    const emailCheck = await this.checkEmailExists(userData.email);
    if (emailCheck.exists) {
      // If email exists with different auth method, link accounts
      if (emailCheck.userId !== userData.uid) {
        throw new Error(`This email is already registered. Please sign in with your existing account or use a different email.`);
      }
    }

    // Create top-level user document (minimal auth data)
    const userRef = doc(db, 'users', userData.uid);
    const topLevelUserData = {
      uid: userData.uid,
      email: userData.email.toLowerCase().trim(), // Normalize email
      displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
      currentFamily: userData.familyCode || null,
      familyCode: userData.familyCode || null, // For backwards compatibility
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(userRef, topLevelUserData);

    // If familyCode is provided, create nested member document
    if (userData.familyCode) {
      const familyRef = doc(db, 'families', userData.familyCode);
      const memberRef = doc(familyRef, 'members', userData.uid);

      const memberData = {
        uid: userData.uid,
        email: userData.email,
        displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone || null,
        role: userData.role || 'member',
        groups: userData.groups || [], // ✅ Groups array
        relationship: userData.relationship,
        invitedBy: userData.invitedBy,
        isActive: userData.isActive !== false,
        isVerified: userData.isVerified || true,
        accountStatus: userData.accountStatus || 'active',
        joinedAt: new Date(),
        updatedAt: new Date(),
        notificationPreferences: userData.notificationPreferences || {
          push: true,
          email: true,
          sms: false,
          emailFrequency: 'instant'
        }
      };

      await setDoc(memberRef, memberData);

      // If this is a family member, establish relationship with family owner
      const familyOwner = await this.getFamilyOwner(userData.familyCode);
      if (familyOwner && familyOwner.id !== userData.uid) {
        await relationshipService.addRelationship(
          familyOwner.id,
          userData.uid,
          userData.relationship || 'member',
          userData.familyCode
        );
      }
    }

    return userData.uid;
  }

  /**
   * Update user profile (NESTED STRUCTURE)
   * Updates both top-level auth and nested member document
   * @param {string} userId - User ID
   * @param {Object} updates - Profile updates
   */
  async updateProfile(userId, updates) {
    if (!userId || !updates) {
      throw new Error('User ID and updates are required');
    }

    // Get user's current family
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const familyCode = primaryFamilyCodeFromUserDoc(userData);

    const updateData = {
      ...updates,
      updatedAt: new Date()
    };

    // Update top-level user document (minimal fields + professional data for directory queries)
    const topLevelUpdates = {};
    if (updates.email) topLevelUpdates.email = updates.email;
    if (updates.displayName) topLevelUpdates.displayName = updates.displayName;
    if (updates.firstName) topLevelUpdates.firstName = updates.firstName;
    if (updates.lastName) topLevelUpdates.lastName = updates.lastName;
    // Sync professional data to top-level for components that query /users directly
    if (updates.professionalProfile !== undefined) topLevelUpdates.professionalProfile = updates.professionalProfile;
    if (updates.businessInfo !== undefined) topLevelUpdates.businessInfo = updates.businessInfo;
    // SOS external contacts + in-app targets: users/{uid} allows self-update; nested member
    // doc was admin-only to update — mirror here so all members can persist emergency data.
    if (updates.emergencyContacts !== undefined) topLevelUpdates.emergencyContacts = updates.emergencyContacts;
    if (updates.emergencyContactIds !== undefined) topLevelUpdates.emergencyContactIds = updates.emergencyContactIds;
    topLevelUpdates.updatedAt = new Date();

    await updateDoc(doc(db, 'users', userId), topLevelUpdates);

    // If user has a family, update nested member document
    if (familyCode) {
      const familyRef = doc(db, 'families', familyCode);
      const memberRef = doc(familyRef, 'members', userId);
      const memberDoc = await getDoc(memberRef);

      if (memberDoc.exists()) {
        await updateDoc(memberRef, updateData);
      }
    }
  }

  /**
   * Get a user profile by ID (NESTED STRUCTURE)
   * Fetches from nested member document if available, falls back to top-level
   * @param {string} userId - User ID to fetch
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const usersCollection = collection(db, 'users');
    const userRef = doc(usersCollection, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // Return null instead of throwing error to handle new user case
      return null;
    }

    const userData = userSnap.data();
    const familyCode = primaryFamilyCodeFromUserDoc(userData);

    // If user has a family, get full profile from nested member document
    if (familyCode) {
      const familyRef = doc(db, 'families', familyCode);
      const memberRef = doc(familyRef, 'members', userId);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        const memberData = memberSnap.data();
        return {
          ...memberData,
          emergencyContacts: memberData.emergencyContacts ?? userData.emergencyContacts ?? [],
          emergencyContactIds: memberData.emergencyContactIds ?? userData.emergencyContactIds ?? [],
          id: memberSnap.id,
          familyCode: familyCode
        };
      }
    }

    // Fall back to top-level user data (for users without families)
    return {
      ...userData,
      id: userSnap.id
    };
  }

  /**
   * Update a user profile (NESTED STRUCTURE - with validation)
   * @param {string} userId - User ID to update
   * @param {Object} updateData - Data to update
   * @returns {Promise<void>}
   */
  async updateUserProfile(userId, updateData) {
    const usersCollection = collection(db, 'users');
    const userRef = doc(usersCollection, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User profile not found');
    }

    const userData = userSnap.data();
    const familyCode = primaryFamilyCodeFromUserDoc(userData);

    // If user has a family, update nested member document
    if (familyCode) {
      const familyRef = doc(db, 'families', familyCode);
      const memberRef = doc(familyRef, 'members', userId);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        // Merge existing member data with updates
        const updatedData = {
          ...memberSnap.data(),
          ...updateData,
          updatedAt: new Date()
        };

        // Validate updated member data
        const validation = validateUserData(updatedData);
        if (!validation.isValid) {
          throw new Error(`Invalid user data: ${validation.errors.join(', ')}`);
        }

        await updateDoc(memberRef, updatedData);
      }
    }

    // Update top-level user document (minimal fields + professional data for directory queries)
    const topLevelUpdates = {};
    if (updateData.email) topLevelUpdates.email = updateData.email;
    if (updateData.displayName) topLevelUpdates.displayName = updateData.displayName;
    if (updateData.firstName) topLevelUpdates.firstName = updateData.firstName;
    if (updateData.lastName) topLevelUpdates.lastName = updateData.lastName;
    if (updateData.currentFamily) topLevelUpdates.currentFamily = updateData.currentFamily;
    if (updateData.familyCode) topLevelUpdates.familyCode = updateData.familyCode;
    // Sync professional data to top-level for components that query /users directly
    if (updateData.professionalProfile !== undefined) topLevelUpdates.professionalProfile = updateData.professionalProfile;
    if (updateData.businessInfo !== undefined) topLevelUpdates.businessInfo = updateData.businessInfo;
    topLevelUpdates.updatedAt = new Date();

    await updateDoc(userRef, topLevelUpdates);
  }

  /**
   * Delete a user profile (NESTED STRUCTURE)
   * Deletes both top-level auth and nested member document
   * @param {string} userId - User ID to delete
   * @returns {Promise<void>}
   */
  async deleteUserProfile(userId) {
    const usersCollection = collection(db, 'users');
    const userRef = doc(usersCollection, userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      const familyCode = primaryFamilyCodeFromUserDoc(userData);

      // If user has a family, delete nested member document first
      if (familyCode) {
        const familyRef = doc(db, 'families', familyCode);
        const memberRef = doc(familyRef, 'members', userId);
        const memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
          await deleteDoc(memberRef);
        }
      }

      // Delete top-level user document
      await deleteDoc(userRef);
    }
  }

  /**
   * Add a family member (NESTED STRUCTURE)
   * Creates nested member document in families/{code}/members/{userId}/
   * @param {Object} memberData - Family member data (must include familyCode)
   * @returns {Promise<string>} ID of created family member
   */
  async addFamilyMember(memberData) {
    if (!memberData.familyCode) {
      throw new Error('Family code is required to add a family member');
    }

    // Validate member data
    const validation = validateFamilyMemberData(memberData);
    if (!validation.isValid) {
      throw new Error(`Invalid family member data: ${validation.errors.join(', ')}`);
    }

    // Create nested member document
    const familyRef = doc(db, 'families', memberData.familyCode);
    const memberRef = doc(familyRef, 'members', memberData.userId);

    const memberDoc = {
      ...createFamilyMemberDocument(memberData),
      groups: memberData.groups || [], // ✅ Initialize groups array
      joinedAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(memberRef, memberDoc);

    return memberData.userId;
  }

  /**
   * Get family members by family code (NESTED STRUCTURE)
   * Queries from families/{code}/members/ subcollection
   * @param {string} familyCode - Family code to fetch members for
   * @returns {Promise<Array>} Array of family members
   */
  async getFamilyMembers(familyCode) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const membersCollection = collection(familyRef, 'members');
    const querySnapshot = await getDocs(membersCollection);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Update a family member (NESTED STRUCTURE)
   * Updates member in families/{code}/members/{userId}/
   * @param {string} familyCode - Family code
   * @param {string} memberId - Member ID to update
   * @param {Object} updateData - Data to update
   * @returns {Promise<void>}
   */
  async updateFamilyMember(familyCode, memberId, updateData) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', memberId);
    const memberSnap = await getDoc(memberRef);

    if (!memberSnap.exists()) {
      throw new Error('Family member not found');
    }

    // Merge existing data with updates
    const updatedData = {
      ...memberSnap.data(),
      ...updateData,
      updatedAt: new Date()
    };

    // Validate updated data
    const validation = validateFamilyMemberData(updatedData);
    if (!validation.isValid) {
      throw new Error(`Invalid family member data: ${validation.errors.join(', ')}`);
    }

    await updateDoc(memberRef, updatedData);
  }

  /**
   * Remove a family member (NESTED STRUCTURE)
   * Deletes member from families/{code}/members/{userId}/
   * @param {string} familyCode - Family code
   * @param {string} memberId - Member ID to remove
   * @returns {Promise<void>}
   */
  async removeFamilyMember(familyCode, memberId) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', memberId);
    const memberSnap = await getDoc(memberRef);

    if (memberSnap.exists()) {
      await deleteDoc(memberRef);

      // Also clear family reference from top-level user document
      const userRef = doc(db, 'users', memberId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        await updateDoc(userRef, {
          currentFamily: null,
          familyCode: null,
          updatedAt: new Date()
        });
      }
    }
  }
}

// Create and export a singleton instance
export const userProfileService = new UserProfileService(); 